import React from 'react';
import { Trash2, Plus, UserPlus } from 'lucide-react';
import { Capacitor } from '@capacitor/core';
import { CapacitorContacts } from '@capgo/capacitor-contacts';
import { ProjectContact } from '@/types';
import { toast } from '@/hooks/use-toast';
import { cn } from '@/lib/utils';

const isAndroid = Capacitor.getPlatform() === 'android';

interface ProjectContactsEditorProps {
  contacts: ProjectContact[];
  onChange: (next: ProjectContact[]) => void;
  showErrors?: boolean;
}

const inputBase =
  'w-full px-3 py-2 rounded-xl border bg-background focus:outline-none focus:ring-2 focus:ring-ring text-sm';

export default function ProjectContactsEditor({
  contacts,
  onChange,
  showErrors = false,
}: ProjectContactsEditorProps) {
  const updateRow = (rowIndex: number, field: keyof ProjectContact, value: string) => {
    onChange(
      contacts.map((c, i) => (i === rowIndex ? { ...c, [field]: value } : c))
    );
  };

  const removeRow = (rowIndex: number) => {
    onChange(contacts.filter((_, i) => i !== rowIndex));
  };

  const addRow = () => {
    const isFirst = contacts.length === 0;
    onChange([
      ...contacts,
      { name: isFirst ? 'איש קשר ראשי' : '', phone: '', role: '' },
    ]);
  };

  const importFromContacts = async () => {
    try {
      const { readContacts } = await CapacitorContacts.requestPermissions({
        permissions: ['readContacts'],
      });
      if (readContacts === 'denied') {
        toast({
          title: 'אין הרשאה לגשת לאנשי הקשר',
          variant: 'destructive',
        });
        return;
      }

      const result = await CapacitorContacts.pickContact({
        fields: ['givenName', 'familyName', 'fullName', 'phoneNumbers'],
      });
      const picked = result.contacts?.[0];
      // User cancelled the native picker — not an error, do nothing.
      if (!picked) return;

      const phone = picked.phoneNumbers?.find((p) => p.value)?.value ?? '';
      if (!phone) {
        toast({
          title: 'לא נמצא מספר טלפון לאיש הקשר שנבחר',
          variant: 'destructive',
        });
        return;
      }

      const name =
        picked.fullName?.trim() ||
        [picked.givenName, picked.familyName].filter(Boolean).join(' ').trim() ||
        phone;

      onChange([...contacts, { name, phone, role: '' }]);
    } catch (err) {
      toast({
        title: 'שגיאה בייבוא איש קשר',
        variant: 'destructive',
      });
    }
  };

  return (
    <div className="space-y-3">
      {contacts.length === 0 && (
        <p className="text-sm text-muted-foreground">אין אנשי קשר עדיין</p>
      )}

      {contacts.map((contact, index) => {
        const nameInvalid = showErrors && !contact.name.trim();
        const phoneInvalid = showErrors && !contact.phone.trim();
        return (
          <div
            key={index}
            className="bg-background rounded-xl border border-border p-3 space-y-2"
          >
            <div className="flex items-start gap-2">
              <button
                type="button"
                onClick={() => removeRow(index)}
                className="w-8 h-8 flex items-center justify-center rounded-lg text-destructive hover:bg-destructive/10 transition-colors flex-shrink-0"
                aria-label="הסר איש קשר"
              >
                <Trash2 className="w-4 h-4" />
              </button>
              <div className="flex-1 grid grid-cols-1 md:grid-cols-2 gap-2">
                <div>
                  <label className="block text-xs text-muted-foreground mb-1">
                    שם איש קשר
                  </label>
                  <input
                    type="text"
                    value={contact.name}
                    onChange={(e) => updateRow(index, 'name', e.target.value)}
                    placeholder="שם איש קשר..."
                    className={cn(inputBase, nameInvalid ? 'border-red-500' : 'border-input')}
                  />
                </div>
                <div>
                  <label className="block text-xs text-muted-foreground mb-1">
                    טלפון
                  </label>
                  <input
                    type="tel"
                    value={contact.phone}
                    onChange={(e) => updateRow(index, 'phone', e.target.value)}
                    placeholder="טלפון..."
                    className={cn(inputBase, phoneInvalid ? 'border-red-500' : 'border-input')}
                  />
                </div>
                <div className="md:col-span-2">
                  <label className="block text-xs text-muted-foreground mb-1">
                    תפקיד (אופציונלי)
                  </label>
                  <input
                    type="text"
                    value={contact.role ?? ''}
                    onChange={(e) => updateRow(index, 'role', e.target.value)}
                    placeholder="תפקיד..."
                    className={cn(inputBase, 'border-input')}
                  />
                </div>
              </div>
            </div>
          </div>
        );
      })}

      <div className="flex flex-wrap items-center gap-2">
        <button
          type="button"
          onClick={addRow}
          className="flex items-center gap-2 px-3 py-2 rounded-xl bg-secondary text-secondary-foreground text-sm font-medium hover:bg-secondary/80 transition-colors"
        >
          <Plus className="w-4 h-4" />
          הוסף איש קשר
        </button>

        {isAndroid && (
          <button
            type="button"
            onClick={importFromContacts}
            className="flex items-center gap-2 px-3 py-2 rounded-xl bg-secondary text-secondary-foreground text-sm font-medium hover:bg-secondary/80 transition-colors"
          >
            <UserPlus className="w-4 h-4" />
            ייבוא מאנשי קשר
          </button>
        )}
      </div>
    </div>
  );
}
